import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import logger from '@/utils/logger'

// Tenant detection and management
let currentTenant = null

/**
 * Extract tenant from subdomain or custom domain
 */
export const detectTenant = () => {
    const hostname = window.location.hostname

    // Development: localhost or 127.0.0.1
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return {
            slug: 'default',
            method: 'localhost'
        }
    }

    // Custom domain pattern (e.g., belliapasticceria.com)
    if (!hostname.includes('tortaonline.com')) {
        return {
            slug: hostname.split('.')[0],
            method: 'custom_domain',
            domain: hostname
        }
    }

    // Subdomain pattern (e.g., bellia.tortaonline.com)
    const parts = hostname.split('.')
    if (parts.length >= 3) {
        return {
            slug: parts[0],
            method: 'subdomain'
        }
    }

    // Main domain (www.tortaonline.com or tortaonline.com)
    return {
        slug: 'www',
        method: 'main_domain'
    }
}

/**
 * Set tenant context
 */
export const setTenant = (tenant) => {
    currentTenant = tenant
    logger.info('API', '🏢 Tenant context set', {
        slug: tenant.slug,
        method: tenant.method
    })
}

/**
 * Get current tenant
 */
export const getTenant = () => {
    if (!currentTenant) {
        currentTenant = detectTenant()
    }
    return currentTenant
}

// Create axios instance
const getBaseURL = () => {
    // Se siamo in sviluppo locale, usa sempre localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'http://localhost:3001/api'
    }
    // In produzione, usa il percorso relativo al dominio corrente
    return '/api'
}

const api = axios.create({
    baseURL: getBaseURL(),
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
})

// Request interceptor con logging completo e tenant awareness
api.interceptors.request.use(
    (config) => {
        // Add tenant headers to all requests
        const tenant = getTenant()
        if (tenant) {
            config.headers['X-Tenant-Slug'] = tenant.slug

            // If we have a tenant ID (from store), add it
            const tenantId = localStorage.getItem('tenant_id')
            if (tenantId) {
                config.headers['X-Tenant-ID'] = tenantId
            }

            // Add custom domain if applicable
            if (tenant.domain) {
                config.headers['X-Tenant-Domain'] = tenant.domain
            }
        }

        // Log della richiesta
        logger.info('API_REQUEST', `🌐 ${config.method?.toUpperCase()} ${config.url}`, {
            method: config.method,
            url: config.url,
            baseURL: config.baseURL,
            params: config.params,
            tenant: tenant?.slug,
            headers: {
                ...config.headers,
                Authorization: config.headers.Authorization ? '[TOKEN_PRESENT]' : '[NO_TOKEN]'
            },
            timeout: config.timeout,
            timestamp: new Date().toISOString()
        })

        // Add auth token if available
        const token = localStorage.getItem('auth_token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
            logger.debug('API_REQUEST', '🔑 Auth token attached to request')
        } else {
            logger.debug('API_REQUEST', '🚫 No auth token available')
        }

        return config
    },
    (error) => {
        logger.error('API_REQUEST', '❌ Request configuration error', {
            error: error.message,
            stack: error.stack
        })
        return Promise.reject(error)
    }
)

// Response interceptor con logging completo
api.interceptors.response.use(
    (response) => {
        // Log della risposta di successo
        logger.info('API_RESPONSE', `✅ ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
            statusText: response.statusText,
            method: response.config.method,
            url: response.config.url,
            responseTime: Date.now() - (response.config.metadata?.startTime || 0),
            dataSize: JSON.stringify(response.data).length,
            headers: response.headers
        })

        const appStore = useAppStore()
        appStore.setLoading(false)
        return response
    },
    async (error) => {
        const appStore = useAppStore()
        const authStore = useAuthStore()

        appStore.setLoading(false)

        // Log errore completo
        logger.error('API_RESPONSE', `❌ API Error: ${error.message}`, {
            method: error.config?.method,
            url: error.config?.url,
            status: error.response?.status,
            statusText: error.response?.statusText,
            errorData: error.response?.data,
            errorMessage: error.message,
            isNetworkError: !error.response,
            timestamp: new Date().toISOString()
        })

        // Handle network errors
        if (!error.response) {
            logger.warn('API_RESPONSE', '📡 Network error - no response from server')
            appStore.showError('Errore di connessione. Verifica la tua connessione internet.')
            return Promise.reject(error)
        }

        const { status, data } = error.response

        // Handle authentication errors - silenzioso
        if (status === 401) {
            logger.warn('API_RESPONSE', '🔒 401 Unauthorized - using offline data')
            // Non mostrare errore all'utente, usa dati offline
        }

        // Handle authorization errors - ridotto
        if (status === 403) {
            logger.warn('API_RESPONSE', '🚫 403 Forbidden - using offline data')
            // Solo per operazioni critiche
            if (error.config.method !== 'get') {
                appStore.showError('Operazione non disponibile. Utilizzo dati locali.')
            }
        }

        // Handle server errors - ridotto
        if (status >= 500) {
            logger.error('API_RESPONSE', `🔥 ${status} Server Error`, { serverError: data })
            // Non mostrare errore per richieste GET
            if (error.config.method !== 'get') {
                appStore.showError('Servizio temporaneamente non disponibile.')
            }
        }

        // Handle rate limiting - silenzioso per ora
        if (status === 429) {
            logger.warn('API_RESPONSE', '⏱️ 429 Rate Limited - backing off')
        }

        return Promise.reject(error)
    }
)

// API helper functions
export const apiHelpers = {
    // Upload file
    async uploadFile(url, file, onProgress) {
        const formData = new FormData()
        formData.append('file', file)

        return api.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
                if (onProgress) {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    )
                    onProgress(percentCompleted)
                }
            }
        })
    },

    // Download file
    async downloadFile(url, filename) {
        try {
            const response = await api.get(url, {
                responseType: 'blob'
            })

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)

            return true
        } catch (error) {
            console.error('Download error:', error)
            return false
        }
    },

    // Paginated request
    async getPaginated(url, params = {}) {
        const response = await api.get(url, { params })
        return {
            data: response.data.data || response.data.items || [],
            pagination: {
                page: response.data.current_page || response.data.page || 1,
                perPage: response.data.per_page || response.data.limit || 20,
                total: response.data.total || 0,
                totalPages: response.data.last_page || response.data.total_pages || 1
            }
        }
    },

    // Batch request
    async batchRequest(requests) {
        return Promise.all(requests.map(request =>
            api({
                method: request.method || 'GET',
                url: request.url,
                data: request.data,
                params: request.params
            })
        ))
    }
}

// Export configured axios instance
export default api
